import { useEffect, useRef, useState } from "react";
import { toast } from "react-toastify";
import SpotifyWebApi from "spotify-web-api-js";
import { SpotifySessionApiError } from "../api";
import { useTokenContext } from "../auth/UserTokenProvider";

const useUser = () => {
  const { token, refreshToken } = useTokenContext();
  const [user, setUser] = useState<SpotifyApi.UserObjectPrivate | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const userRef = useRef(user);
  userRef.current = user;

  // Request the user when the token changes
  useEffect(() => {
    let didCancel = false;
    const fetchUser = async (accessToken: string, allowRefresh = true) => {
      const spotifyApi = new SpotifyWebApi();
      spotifyApi.setAccessToken(accessToken);
      try {
        const newUser = await spotifyApi.getMe();
        if (!didCancel) {
          setUser(newUser);
          setIsLoading(false);
        }
      } catch (err: any) {
        if (didCancel) {
          return;
        }

        if (allowRefresh && err?.status === 401) {
          try {
            const refreshed = await refreshToken();
            if (refreshed) {
              await fetchUser(refreshed.value, false);
              return;
            }
          } catch (refreshError) {
            console.error(refreshError);
            if (
              refreshError instanceof SpotifySessionApiError &&
              refreshError.status >= 400 &&
              refreshError.status < 500 &&
              refreshError.status !== 429
            ) {
              setUser(null);
              userRef.current = null;
            }
          }
        }

        console.error(err);
        setIsLoading(false);
        toast.error(
          "Could not get your profile. Make sure you are connected to the internet and that cookies are allowed.",
          {
            position: "bottom-center",
            autoClose: 5000,
            onClick: () => toast.dismiss()
          }
        );
      }
    };

    if (token === null) {
      setUser(null);
      userRef.current = null;
      setIsLoading(false);
    } else {
      if (userRef.current === null) {
        setIsLoading(true);
      }
      void fetchUser(token.value);
    }

    return () => {
      didCancel = true;
    };
  }, [token, refreshToken]);

  return { user, isLoading };
};

export default useUser;
